import React from "react";
import { Typography, Space, Row, Col, Flex } from "antd";
import "./index.css";
import {
  capitalizeTranslated,
  LLFormatWithTime,
  utcDateTimeToLocalPA,
} from "../../../shared";

const { Text, Title } = Typography;

const NewPrescription = ({ state, medicinesLookup, sessions }) => {
  const medical = state?.appointment?.client_info?.medical_info;

  const isYes = (value) => {
    if (value == null) return false;

    const normalized = String(value).trim().toLowerCase();

    return normalized === "1" || normalized === "yes" || normalized === "true";
  };

  const height = medical?.height;
  const weight = medical?.weight;

  const drugs_allergy = medical?.drug_allergies ?? "N/A";
  const food_allergy = medical?.food_allergies ?? "N/A";
  const diagnosis = state?.service?.name ?? "N/A";
  const notes = medical?.note ?? "N/A";

  const gender = state?.appointment?.client_info?.gender ?? "N/A";

  // Diseases / Conditions
  const diabetes = isYes(medical?.isDiabetes) ? "Diabetes: Yes" : null;

  const hypertension = isYes(medical?.isHypertension)
    ? "Hypertension: Yes"
    : null;

  const jaundice = isYes(medical?.isJaundice) ? "Jaundice: Yes" : null;

  const asthma = isYes(medical?.isAsthma) ? "Asthma: Yes" : null;

  const epilepsy = isYes(medical?.isEpilepsy) ? "Epilepsy: Yes" : null;

  const familyDisease = isYes(medical?.isFamilyDisease)
    ? "Family Disease: Yes"
    : null;

  const hepatitis = isYes(medical?.isHepatitis) ? "Hepatitis: Yes" : null;

  const ihd = isYes(medical?.isIHD) ? "IHD: Yes" : null;

  const skinConditions = isYes(medical?.isSkinConditions)
    ? "Skin Conditions: Yes"
    : null;

  const smoking = isYes(medical?.smoking) ? "Smoking: Yes" : null;

  const aspirin = isYes(medical?.anyAspirin) ? "Aspirin Use: Yes" : null;

  const steroids = isYes(medical?.anySteroids) ? "Steroids Use: Yes" : null;

  // Vitals
  const pulse =
    medical?.pulse != null ? `Pulse: ${medical.pulse} min` : "Pulse: N/A";

  const bp = medical?.BP != null ? `B.P: ${medical.BP} mmHg` : "B.P: N/A";

  const bsr = medical?.BSR != null ? `BSR: ${medical.BSR} mg/dl` : "BSR: N/A";

  const spo2 =
    medical?.oxygen_Level != null
      ? `Oxygen Level SpO2: ${medical?.oxygen_Level}%`
      : "SpO2: N/A";

  // Infection / Blood Tests
  const hiv = isYes(medical?.HIV) ? "HIV: Positive" : null;

  const hepatitisB = isYes(medical?.hepatitisB)
    ? "Hepatitis B: Positive"
    : null;

  const hepatitisC = isYes(medical?.hepatitisC)
    ? "Hepatitis C: Positive"
    : null;

  // Final Array
  const vitalSigns = [
    pulse,
    bp,
    bsr,
    spo2,
    diabetes,
    hypertension,
    jaundice,
    asthma,
    epilepsy,
    hepatitis,
    ihd,
    hiv,
    hepatitisB,
    hepatitisC,
  ].filter(Boolean);
  const bmi =
    height && weight ? (weight / (height / 100) ** 2).toFixed(2) : null;

  return (
    <div className="dflex">
      <div className="w-h prescription">
        <Space className="w-100" direction="vertical" size={2}>
          <div style={{ paddingBottom: 120 }}>
            <div>
              {/* Header */}
              <header className="pres-header">
                <Space className="w-50" direction="vertical" size={5}>
                  <img src="/assets/images/logo.webp" alt="logo" width={200} />

                  <Space direction="vertical" size={1}>
                    <Flex gap={5} className="w-100">
                      <Text strong className="fs-12" style={{ width: 90 }}>
                        Address:
                      </Text>

                      <Text className="fs-12">
                        1st Floor, Nazeer Plaza, Chandni Chowk, Murree Road
                        Above Bank Al-Falah Islamic, Rawalpindi.
                      </Text>
                    </Flex>

                    <Flex gap={5}>
                      <Text strong className="fs-12" style={{ width: 50 }}>
                        Tel:
                      </Text>

                      <Text className="fs-12">+92 300 5029677</Text>
                    </Flex>
                  </Space>
                </Space>
              </header>

              {/* Patient Table */}
              <table className="pres-table" style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th
                      style={{
                        textAlign: "center",
                        fontSize: 12,
                        width: "16%",
                      }}
                    >
                      MRN #
                    </th>

                    <th
                      style={{
                        textAlign: "center",
                        fontSize: 12,
                        width: "28%",
                      }}
                    >
                      Name
                    </th>

                    <th
                      style={{
                        textAlign: "center",
                        fontSize: 12,
                        width: "12%",
                      }}
                    >
                      BMI
                    </th>

                    <th
                      style={{
                        textAlign: "center",
                        fontSize: 12,
                        width: "12%",
                      }}
                    >
                      Height
                    </th>

                    <th
                      style={{
                        textAlign: "center",
                        fontSize: 12,
                        width: "12%",
                      }}
                    >
                      Weight
                    </th>

                    <th
                      style={{
                        textAlign: "center",
                        fontSize: 12,
                        width: "20%",
                      }}
                    >
                      Visit Date
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <tr>
                    <td className="fs-11" style={{ textAlign: "center" }}>
                      {state?.appointment?.client_info?.mrn ?? "N/A"}
                    </td>

                    <td className="fs-11" style={{ textAlign: "center" }}>
                      {`${state?.name ?? "N/A"} ${
                        state?.age != null ? state.age + "y" : "N/A"
                      } ${capitalizeTranslated(gender)}`}
                    </td>

                    <td className="fs-11" style={{ textAlign: "center" }}>
                      {bmi ? `${bmi} kg/m²` : "N/A"}
                    </td>

                    <td className="fs-11" style={{ textAlign: "center" }}>
                      {height ? height + " Cm" : "N/A"}
                    </td>

                    <td className="fs-11" style={{ textAlign: "center" }}>
                      {weight ? weight + " Kg" : "N/A"}
                    </td>

                    <td className="fs-11" style={{ textAlign: "center" }}>
                      {LLFormatWithTime()}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Main Content */}
              <Flex className="pres-header" gap={20} style={{ marginTop: 20 }}>
                {/* Left Side */}
                <div className="w-70">
                  <Space
                    className="w-70"
                    direction="vertical"
                    size={10}
                    style={{ marginBottom: 50 }}
                  >
                    <Flex gap={10} className="pres-header">
                      <div className="w-50">
                        <Flex vertical gap={1} className="w-100">
                          <Text strong className="m-0 fs-12">
                            Drug Allergies:
                          </Text>

                          <Text style={{ paddingLeft: 10 }} className="fs-12">
                            {drugs_allergy}
                          </Text>
                        </Flex>
                      </div>

                      <div className="w-50">
                        <Flex vertical justify="end" gap={1} className="w-100">
                          <Text strong className="m-0 fs-12">
                            Diagnosis/ Procedure:
                          </Text>

                          <Text
                            style={{
                              paddingLeft: 10,
                              borderLeft: "1px solid black",
                            }}
                            className="fs-12"
                          >
                            {diagnosis}
                          </Text>
                        </Flex>
                      </div>
                    </Flex>

                    <Flex vertical gap={1}>
                      <Text strong className="m-0 fs-12">
                        Food Allergies:
                      </Text>

                      <Text style={{ paddingLeft: 10 }} className="fs-12">
                        {food_allergy}
                      </Text>
                    </Flex>
                  </Space>

                  {/* Medicines */}
                  <div>
                    <Text>Rx</Text>

                    <div>
                      {state?.medicines?.map((medicine, index) => (
                        <div
                          key={index}
                          className="mt-3 w-100"
                          style={{
                            borderBottom: "1px solid lightgray",
                          }}
                        >
                          <Row gutter={[16, 16]}>
                            <Col span={12}>
                              <Text className="fs-12">{index + 1}-</Text>{" "}
                              <Text className="fs-12">
                                {medicine?.medicineName}
                              </Text>
                            </Col>

                            <Col span={12}>
                              <Flex justify="end">
                                <Text
                                  className="fs-12"
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {medicine?.notes}
                                </Text>
                              </Flex>
                            </Col>
                          </Row>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right Side */}
                <div
                  className="w-30"
                  style={{
                    borderLeft: "1px solid lightgray",
                    paddingLeft: 10,
                  }}
                >
                  <Flex vertical gap={10}>
                    <Flex vertical gap={1}>
                      <Text strong className="m-0 fs-12">
                        Vital Signs:
                      </Text>

                      <Text className="fs-12">
                        {vitalSigns.length ? vitalSigns.join(", ") : "N/A"}
                      </Text>
                    </Flex>

                    <Flex vertical gap={1}>
                      <Text strong className="m-0 fs-12">
                        Notes:
                      </Text>

                      <Text className="fs-12">{notes}</Text>
                    </Flex>

                    {/* <Flex vertical gap={1}>
                      <Text strong className="m-0 fs-12">
                        Next Session:
                      </Text>

                      <Text className="fs-12">
                        {sessions?.sessionDateTime
                          ? utcDateTimeToLocalPA(sessions?.sessionDateTime)
                          : "Last Session"}
                      </Text>
                    </Flex> */}
                  </Flex>
                </div>
              </Flex>
            </div>
          </div>

          {/* Footer */}
          <footer className="pres-footer">
            <Flex vertical gap={0} style={{ marginTop: -5 }}>
              <Title level={4} className="m-0">
                Dr. Fawad Aamir
              </Title>

              <Text className="fs-12">PM&DC Reg. No 9117-P</Text>

              <Text className="fs-12">MBBS, FCPS</Text>

              <Text className="fs-12">Consultant Surgeon</Text>

              <Text className="fs-12">Hair Transplant Surgeon</Text>
            </Flex>

            <Flex justify="end" vertical size={10}>
              <Flex gap={5} justify="end" className="w-100">
                <Text className="fs-12">
                  براہ کرم مشورہ کے بغیر دوائی بند، کم ، یا تبدیل نہ کریں
                </Text>

                <Text strong className="fs-12">
                  :ہدایات
                </Text>
              </Flex>

              <Flex gap={5} justify="end" className="w-100">
                <Text className="fs-12">
                  Make an appointment before visit,
                  <strong> Follow Up</strong>
                </Text>

                <Text strong className="fs-12">
                  :چیک اپ
                </Text>
              </Flex>

              <Flex align="center" justify="end" gap={10}>
                <img
                  src="/assets/images/hpi-2.0.png"
                  alt="qr code"
                  width={70}
                />

                <Flex vertical align="flex-end" gap={5}>
                  <Text className="fs-12">
                    This is a computer-generated prescription; no signature is
                    required.
                  </Text>

                  <Flex gap={10}>
                    <Space size={3} align="center">
                      <img
                        src="/assets/icons/whatsapp.png"
                        style={{ flexShrink: 0 }}
                        width={13}
                      />

                      <Text className="fs-12">+92 300 5029677</Text>
                    </Space>

                    <Text className="fs-12">
                      -مزید معلومات کے لیے دیے گئے نمبر پر رابطہ کریں
                    </Text>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          </footer>
        </Space>
      </div>
    </div>
  );
};

export { NewPrescription };
